import { create } from 'zustand';
import { getReport } from '../../../services/patients/patientsService';
import { formatDate } from '../../../utils/date';
import { getSales } from '../../../services/sales/salesService';
import { getOxygenConsumptionKpi, getCurrentRentalValueKpi } from '../../../services/reports/kpiService';

const date = new Date();
date.setMonth(new Date().getMonth());

export const useKPIStore = create<any>((set: any, get: any) => ({
    selectedCategory: '',
    selectedCPF: null,
    oxygenConsumed: 0,
    currentRentalValue: 0,
    selectedDate: { month: date.getMonth(), year: date.getFullYear() },
    rows: 0,
    page: 0,
    pageSize: 10,
    patients: [],

    setSelectedCategory: (selectedCategory: any) => set({ selectedCategory }),

    setCPF: (cpf: any) => {
        set({ selectedCPF: cpf });
    },

    setPagination: (page: number, pageSize: number) => {
        set({ page, pageSize });
    },

    setSelectedDate: (month: number, year: number) => {
        set({
            selectedDate: { month, year },
            page: 0,
        });
    },

    fetchPatientsKpi: async () => {
        const startDate = new Date(get().selectedDate.year, get().selectedDate.month, 1);

        const endDate = new Date(get().selectedDate.year, get().selectedDate.month, 1);

        endDate.setMonth(get().selectedDate.month + 1);
        endDate.setDate(endDate.getDate() - 1);

        const formattedStartDate = formatDate(startDate);
        const formattedEndDate = formatDate(endDate);

        // KPI de consumo mensal de oxigênio:
        const oxygenConsumptionPromise = getOxygenConsumptionKpi({
            startDate: formattedStartDate,
            endDate: formattedEndDate,
        });

        // KPI de valor de locação atual:
        const currentRentalValuePromise = getCurrentRentalValueKpi({
            startDate: formattedStartDate,
            endDate: formattedEndDate,
        });

        const resp: any = await getReport(get().selectedCPF, get().page, get().pageSize, formattedStartDate, formattedEndDate);

        const promisesData = resp.data.map(async (patient: any) => {
            patient.equipments = patient.reports;

            patient.additionalInformation = [];

            delete patient.reports;

            return patient;
        });

        const [oxygenConsumption, currentRentalValue] = await Promise.all([
            oxygenConsumptionPromise,
            currentRentalValuePromise,
        ]);

        const totalOxygenConsumed = Number(oxygenConsumption?.oxygenConsumed || 0);

        const totalCurrentRentalValue = Number(currentRentalValue?.currentRentalValue || 0);

        resp.data = await Promise.all(promisesData);

        set({
            patients: [...resp.data],
            page: resp.page,
            pageSize: resp.pagesize,
            rows: resp.rows,
            oxygenConsumed: totalOxygenConsumed,
            currentRentalValue: totalCurrentRentalValue,
        });
    },

    fetchPatientsToReport: async () => {
        const startDate = new Date(get().selectedDate.year, get().selectedDate.month, 1);

        const endDate = new Date(get().selectedDate.year, get().selectedDate.month, 1);

        endDate.setMonth(get().selectedDate.month + 1);
        endDate.setDate(endDate.getDate() - 1);

        const formattedStartDate = formatDate(startDate);
        const formattedEndDate = formatDate(endDate);

        const salesPromise = getSales(get().selectedCPF, 0, 10000000, formattedStartDate, formattedEndDate);

        const p: any = await getReport(get().selectedCPF, 0, 10000000, formattedStartDate, formattedEndDate);

        const rows = p.rows;
        const pageSize = p.pagesize;
        const totalMonth = endDate.getDate() - 1;
        const totalPages = parseInt(String(rows / pageSize), 10) + 1;

        const sales: any = await salesPromise;

        const totalOxygenConsumed = sales.data.map((x: any) => Number(x.vol_total) || 0).reduce((acc: number, x: number) => acc + x, 0);

        const patientsList: any[] = [];

        p.data.forEach((patient: any) => {
            patient.reports.forEach((report: any) => {
                const reportData = { ...patient, ...report };

                delete reportData.reports;

                patientsList.push(reportData);
            });
        });

        if (rows < pageSize) {
            return {
                oxygenConsumed: totalOxygenConsumed,
                currentRentalValue: p.currentRentalValue,
                currentLeasing: parseFloat(
                    patientsList
                        .map((x: any) => (!isNaN(parseFloat(x.monthly_lease)) ? parseFloat(x.monthly_lease) : 0))
                        .reduce((x: number, acc: number) => x + acc, 0)
                        .toFixed(2)
                ),
                patients: patientsList,
                totalMonth: totalMonth,
                year: get().selectedDate.year,
                month: get().selectedDate.month + 1,
            };
        }

        const results = await Promise.all(
            Array.from({ length: totalPages - 1 })
                .map((x, i) => i + 1)
                .map((page: number) => getReport(get().selectedCPF, page, pageSize, formattedStartDate, formattedEndDate))
        );

        for (const r of results) {
            p.data = p.data.concat(r.data);
        }

        return {
            oxygenConsumed: totalOxygenConsumed,
            currentRentalValue: p.currentRentalValue,
            currentLeasing: parseFloat(
                patientsList
                    .map((x: any) => (!isNaN(parseFloat(x.monthly_lease)) ? parseFloat(x.monthly_lease) : 0))
                    .reduce((x: number, acc: number) => x + acc, 0)
                    .toFixed(2)
            ),
            patients: patientsList,
            totalMonth: totalMonth,
            year: get().selectedDate.year,
            month: get().selectedDate.month + 1,
        };
    },
}));
